import { useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router";

import { PROTECTED_ROUTES, registrarRecordDestination } from "@/navigation/routes";

/**
 * The entry for every registrar route a retired address can arrive at, the
 * registrar's `SeatRecordEntry`.
 *
 * Only the ARRIVAL is resolved: the destination is read once per mount and
 * acted on only while the location is still the arrival, so a page's own
 * `setSearchParams` is never re-decided.
 *
 * `children` is the page for an arrival that is itself a destination: the
 * Courses, People, Admissions, and Invitations lists, which each used to open
 * a record over themselves, and the Faculty lookup, the one retired arrival
 * only the server can resolve. A route with no page of its own lands on the
 * Registrar Workspace.
 */
export function RegistrarRecordEntry({ children }: { children?: ReactNode }) {
  const location = useLocation();
  const [arrival] = useState(() => ({
    key: location.key,
    destination: registrarRecordDestination(location.pathname, location.search, location.hash),
  }));
  if (arrival.destination && location.key === arrival.key) {
    return <Navigate to={arrival.destination} replace />;
  }
  return children ?? <Navigate to={PROTECTED_ROUTES.admin.dashboard} replace />;
}
